class Toast {
  private container: HTMLDivElement | null = null;
  private activeMessages: Set<string> = new Set();

  private ensureContainer() {
    if (this.container) return;
    const el = document.createElement("div");
    el.id = "app-toast-container";
    Object.assign(el.style, {
      position: "fixed",
      top: "80px",
      right: "10px",
      display: "flex",
      flexDirection: "column",
      gap: "8px",
      zIndex: "9999",
      pointerEvents: "none",
    });
    document.body.appendChild(el);
    this.container = el as HTMLDivElement;
  }

  private makeToastElement(
    message: string,
    type: "success" | "error" | "info" | "warning"
  ) {
    const colors: Record<string, { bg: string; border: string; text: string }> =
      {
        success: { bg: "#1B5E20", border: "#8BC34A", text: "#E8F5E9" },
        error: { bg: "#B71C1C", border: "#EF9A9A", text: "#FFEBEE" },
        info: { bg: "#0D47A1", border: "#90CAF9", text: "#E3F2FD" },
        warning: { bg: "#F57F17", border: "#FFD54F", text: "#FFF8E1" },
      };
    const palette = colors[type] ?? colors.info;

    const item = document.createElement("div");
    Object.assign(item.style, {
      background: palette.bg,
      color: palette.text,
      border: `1px solid ${palette.border}`,
      borderRadius: "8px",
      padding: "12px 16px",
      boxShadow: "0 6px 24px rgba(0,0,0,0.24), 0 0 0 1px rgba(0,0,0,0.08)",
      maxWidth: "360px",
      minWidth: "220px",
      fontFamily:
        "system-ui, -apple-system, Segoe UI, Roboto, Helvetica, Arial, sans-serif",
      fontSize: "14px",
      lineHeight: "1.4",
      pointerEvents: "auto",
      opacity: "0",
      transform: "translateY(-8px)",
      transition: "opacity .2s ease, transform .2s ease",
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      gap: "12px",
    } as CSSStyleDeclaration);

    const messageEl = document.createElement("span");
    messageEl.textContent = message;
    messageEl.style.flex = "1";
    item.appendChild(messageEl);

    const closeBtn = document.createElement("button");
    closeBtn.innerHTML = "&times;";
    Object.assign(closeBtn.style, {
      background: "transparent",
      border: "none",
      color: "inherit",
      fontSize: "20px",
      fontWeight: "300",
      cursor: "pointer",
      padding: "0 4px",
      lineHeight: "1",
      opacity: "0.7",
      transition: "opacity 0.2s",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
    });
    closeBtn.addEventListener("mouseenter", () => (closeBtn.style.opacity = "1"));
    closeBtn.addEventListener("mouseleave", () => (closeBtn.style.opacity = "0.7"));
    item.appendChild(closeBtn);

    // mount animation
    requestAnimationFrame(() => {
      item.style.opacity = "1";
      item.style.transform = "translateY(0)";
    });

    return { item, closeBtn };
  }

  private show(
    message: string,
    type: "success" | "error" | "info" | "warning" = "info",
    duration = 3000
  ) {
    if (!message) return;
    // Deduplicate: skip if the same message is already visible
    const key = `${type}:${message}`;
    if (this.activeMessages.has(key)) return;
    this.activeMessages.add(key);

    this.ensureContainer();
    const { item, closeBtn } = this.makeToastElement(message, type);
    this.container!.appendChild(item);

    const remove = () => {
      this.activeMessages.delete(key);
      item.style.opacity = "0";
      item.style.transform = "translateY(-8px)";
      setTimeout(() => {
        item.remove();
      }, 200);
    };

    const timer = setTimeout(remove, duration);

    // close on button click
    closeBtn.addEventListener("click", (e) => {
      e.stopPropagation();
      clearTimeout(timer);
      remove();
    });

    // close on toast click (optional, but keep it for UX)
    item.addEventListener("click", (e) => {
      if (e.target !== closeBtn) {
        clearTimeout(timer);
        remove();
      }
    });
  }

  success(message: string, duration?: number) {
    this.show(message, "success", duration);
  }
  error(message: string, duration?: number) {
    this.show(message, "error", duration);
  }
  info(message: string, duration?: number) {
    this.show(message, "info", duration);
  }
  warning(message: string, duration?: number) {
    this.show(message, "warning", duration);
  }
}

export const toast = new Toast();
